import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiFileText, FiImage, FiLoader } from 'react-icons/fi';
import { CMR_EXTRACTION_PROGRESS_STEPS } from '../../constants/cmrExtractionProgressSteps';

const FAST_COMPLETE_MS = 400;

function StepIcon({ status }) {
  if (status === 'done') {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
        <FiCheck className="h-4 w-4" />
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
        <FiLoader className="h-4 w-4 animate-spin" />
      </span>
    );
  }
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-300">
      <span className="h-2 w-2 rounded-full bg-gray-300" />
    </span>
  );
}

function getStepStatus(index, completedThrough, activeIndex) {
  if (index <= completedThrough) return 'done';
  if (index === activeIndex) return 'active';
  return 'pending';
}

export default function CmrExtractionProgress({ fileName, isPdf, apiFinished, apiSuccess }) {
  const steps = CMR_EXTRACTION_PROGRESS_STEPS;
  const totalSteps = steps.length;
  const timersRef = useRef([]);
  const stepRefs = useRef([]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [completedThrough, setCompletedThrough] = useState(-1);
  const [finishing, setFinishing] = useState(false);

  const progressPct = useMemo(() => {
    if (finishing && apiSuccess) return 100;
    const base = ((completedThrough + 1) / totalSteps) * 100;
    const activeBump = activeIndex > completedThrough ? 8 : 0;
    return Math.min(98, Math.max(4, base + activeBump));
  }, [completedThrough, activeIndex, finishing, apiSuccess, totalSteps]);

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (apiFinished) return undefined;

    setFinishing(false);
    setActiveIndex(0);
    setCompletedThrough(-1);

    let idx = 0;
    const scheduleNext = () => {
      if (idx >= totalSteps) return;
      const step = steps[idx];
      const t = setTimeout(() => {
        if (idx > 0) setCompletedThrough(idx - 1);
        setActiveIndex(idx);
        idx += 1;
        if (idx < totalSteps) {
          scheduleNext();
        } else {
          setCompletedThrough(totalSteps - 2);
          setActiveIndex(totalSteps - 1);
        }
      }, step.minDurationMs);
      timersRef.current.push(t);
    };
    scheduleNext();

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [apiFinished, steps, totalSteps]);

  useEffect(() => {
    if (!apiFinished || !apiSuccess) return undefined;

    setFinishing(true);
    let i = activeIndex;
    const flush = () => {
      if (i >= totalSteps - 1) {
        setCompletedThrough(totalSteps - 1);
        setActiveIndex(totalSteps - 1);
        return;
      }
      setCompletedThrough(i);
      i += 1;
      setActiveIndex(i);
      setTimeout(flush, FAST_COMPLETE_MS);
    };
    flush();
    return undefined;
  }, [apiFinished, apiSuccess, totalSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const stepEl = stepRefs.current[activeIndex];
    if (!stepEl) return undefined;

    const scrollActiveIntoView = () => {
      stepEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    };

    const t = setTimeout(() => {
      requestAnimationFrame(scrollActiveIntoView);
    }, 80);

    return () => clearTimeout(t);
  }, [activeIndex, completedThrough]);

  return (
    <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-white via-white to-emerald-50/40 p-5 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {isPdf ? <FiFileText size={20} /> : <FiImage size={20} />}
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-primary tracking-widest uppercase">
            CMR extraction in progress
          </p>
          <p className="text-sm font-medium text-gray-900 truncate mt-0.5" title={fileName}>
            {fileName}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            This usually takes 15–30 seconds. Please keep this tab open.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1.5">
          <span>Overall progress</span>
          <span>{Math.round(progressPct)}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div
        role="status"
        aria-live="polite"
        className="cmr-extraction-steps-scroll max-h-[min(320px,50vh)] min-h-[200px] overflow-y-auto scroll-smooth rounded-lg border border-gray-100 bg-white/60 py-2 pr-1 pl-1"
      >
        <ul className="space-y-1.5">
          {steps.map((step, index) => {
            const status = getStepStatus(index, completedThrough, activeIndex);
            const isActive = status === 'active';
            return (
              <li
                key={step.id}
                ref={(node) => {
                  stepRefs.current[index] = node;
                }}
                className={`scroll-my-3 flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-all
                  ${isActive ? 'border-primary/25 bg-primary/5 shadow-sm' : 'border-transparent'}
                  ${status === 'done' ? 'bg-emerald-50/40' : ''}`}
              >
                <StepIcon status={status} />
                <div className="min-w-0 pt-0.5">
                  <p
                    className={`text-sm font-semibold leading-tight ${
                      status === 'done'
                        ? 'text-emerald-700'
                        : isActive
                          ? 'text-gray-900'
                          : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </p>
                  {isActive && step.detail && (
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">{step.detail}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
